import { lazy } from 'react'

import { ErrorBoundary } from 'react-error-boundary'
import { createBrowserRouter } from 'react-router'

import { RegionLoadingOverlay } from '@/components/region-loading-overlay'
import { RegionLoadingProvider } from '@/contexts/region-loading'
import { brasilAtendimentoRoutes } from '@/features/atendimento/brasil/routes'
import { chileAtendimentoRoutes } from '@/features/atendimento/chile/routes'
import { brasilEmpreendimentosRoutes } from '@/features/empreendimentos/brasil/routes'
import { chileEmpreendimentosRoutes } from '@/features/empreendimentos/chile/routes'
import { brasilEspelhoDeVendasRoutes } from '@/features/espelho-de-vendas/brasil/routes'
import { chileEspelhoDeVendasRoutes } from '@/features/espelho-de-vendas/chile/routes'
import { brasilHistoricoRoutes } from '@/features/historico/brasil/routes'
import { chileHistoricoRoutes } from '@/features/historico/chile/routes'
import { brasilLeadsRepresadoRoutes } from '@/features/leads-represados/brasil/routes'
import { brasilPropostaRoutes } from '@/features/proposta/brasil/routes'
import { chilePropostaRoutes } from '@/features/proposta/chile/routes'
import { chileSimulacaoRapidaRoutes } from '@/features/simulacao-rapida/chile/routes'
import { brasilSimuladorRoutes } from '@/features/simulador/brasil/routes'
import { chileSimuladorRoutes } from '@/features/simulador/chile/routes'
import { ventasApi } from '@/infra'
import { AxiosInterceptor } from '@/infra/http/interceptors/axios-interceptor'
import { RootLayout } from '@/layouts/root-layout'
import { SimulacaoPage } from '@/pages/simulacao'
import { AuthProvider } from '@/providers'
import { CountryGuard } from '@/routes/guards'
import { ErrorPage } from '@/shared/error'

import { ConfiguracoesParametrosPageSuspense, ConfiguracoesReservaPageSuspense } from './pages-suspense'
import { PrivateRoute } from './private'

const LoginPage = lazy(() =>
  import('@/features/login/shared/pages/login').then((module) => ({ default: module.LoginPage })),
)

const NotFoundPage = lazy(() =>
  import('@/shared/not-found').then((module) => ({
    default: module.NotFoundPage,
  })),
)
const ConfiguracoesPerfilPage = lazy(() =>
  import('@/pages/configuracoes/perfil').then((module) => ({ default: module.ConfiguracoesPerfilPage })),
)
const ConfiguracoesReservaPage = lazy(() =>
  import('@/pages/configuracoes/reserva').then((module) => ({ default: module.ConfiguracoesReservaPage })),
)
const ConfiguracoesParametrosPage = lazy(() =>
  import('@/pages/configuracoes/parametros').then((module) => ({ default: module.ConfiguracoesParametrosPage })),
)
const ConfiguracoesTermosPage = lazy(() =>
  import('@/pages/configuracoes/termos').then((module) => ({ default: module.ConfiguracoesTermosPage })),
)
const ConfiguracoesCoresLotesPage = lazy(() =>
  import('@/pages/configuracoes/cores-lotes').then((module) => ({ default: module.ConfiguracoesCoresLotesPage })),
)

const commonRoutes = [
  {
    path: 'simulacao/:id',
    element: <SimulacaoPage />,
  },
  {
    path: 'configuracoes',
    children: [
      {
        path: 'cores-lotes',
        element: <ConfiguracoesCoresLotesPage />,
      },
      {
        path: 'reserva',
        element: (
          <ConfiguracoesReservaPageSuspense>
            <ConfiguracoesReservaPage />
          </ConfiguracoesReservaPageSuspense>
        ),
      },
      {
        path: 'parametros',
        element: (
          <ConfiguracoesParametrosPageSuspense>
            <ConfiguracoesParametrosPage />
          </ConfiguracoesParametrosPageSuspense>
        ),
      },
      {
        path: 'perfil',
        element: <ConfiguracoesPerfilPage />,
      },
      {
        path: 'termos',
        element: <ConfiguracoesTermosPage />,
      },
    ],
  },
]

const chileRoutes = [
  ...chileAtendimentoRoutes,
  ...chileEmpreendimentosRoutes,
  ...chileEspelhoDeVendasRoutes,
  ...chileSimuladorRoutes,
  ...chileSimulacaoRapidaRoutes,
  ...chilePropostaRoutes,
  ...chileHistoricoRoutes,
]
const brasilRoutes = [
  ...brasilAtendimentoRoutes,
  ...brasilEmpreendimentosRoutes,
  ...brasilEspelhoDeVendasRoutes,
  ...brasilSimuladorRoutes,
  ...brasilPropostaRoutes,
  ...brasilLeadsRepresadoRoutes,
  ...brasilHistoricoRoutes,
]

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ErrorBoundary fallbackRender={ErrorPage}>
        <RegionLoadingProvider>
          <AuthProvider>
            <RegionLoadingOverlay />
            <RootLayout />
          </AuthProvider>
        </RegionLoadingProvider>
      </ErrorBoundary>
    ),
    children: [
      {
        path: 'login',
        element: <LoginPage />,
      },
      {
        path: '',
        element: (
          <AxiosInterceptor api={ventasApi}>
            <CountryGuard>
              <PrivateRoute />
            </CountryGuard>
          </AxiosInterceptor>
        ),
        children: [...commonRoutes, ...brasilRoutes, ...chileRoutes],
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
])
